import type {UserDetails, PaymentDetails} from 'src/types/referrer-portal';
import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {useHistory} from 'src/rerouter';
import {useDispatch, useSelector} from 'react-redux';
import {useInflight} from 'src/hooks/redux';

import * as reduxApi from 'src/utils/redux-api-referrer-portal';

import {getCountryCallingCode} from 'libphonenumber-js/max';
import {validatePhone, getCountryFromPhone} from 'src/utils/phone';
import {
  getUserFormInputFields,
  PROFILE_SETUP_STEEPER,
  paymentInitialData,
  userFormInitialValue,
} from 'src/utils/referrer-portal';
import classify from 'src/utils/classify';
import {
  getUserDetails,
  createPaymentDetails,
} from 'src/action-creators/referrer-portal/authentication';
import {selectUserDetails} from 'src/selectors/referrer-portal';

import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardHeader,
  CardContent,
  SubTitleLarge,
  BodyLarge,
  SubTitleMedium,
  Input,
  Button,
  Textarea,
  Stepper,
  StepLabel,
  Step,
  CircularLoader,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/index';
import UploadProfile from 'src/components/referrer-portal/settings/profile/upload-profile-pic';
import PaymentCard from 'src/components/referrer-portal/earnings/edit-payment-card';
import {PhoneInputQuestion} from 'src/components/referrer-portal/user-form/user-form';
import {toastMessage} from 'src/components/referral-v2/tracking/referral-confirm-modal';

import css from 'src/components/referrer-portal/settings/settings.css';


const SetupProfile = () => {
  const {t} = useLabelTranslation();
  const USER_FORM_INPUT_FIELDS = getUserFormInputFields(t);
  const router = useHistory();
  const dispatch = useDispatch();

  const [isSetup, setIsSetup] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errors, setErrors] = React.useState(new Map());
  const [selectedTab, setSelectedTab] = React.useState<'profile' | 'payment'>(
    'profile',
  );
  const [formData, setFormData] =
    React.useState<UserDetails>(userFormInitialValue);
  const [paymentData, setPaymentData] =
    React.useState<PaymentDetails>(paymentInitialData);

  const isProfileConfig = selectedTab === 'profile';

  const saveUserDetails = () => {
    setIsSubmitting(true);
    const contact =
      // here number format is coming in array with country code and number
      getDefaultPhoneNumber(formData.phone_number).filter((item) => item)
        .length === 2
        ? formData.phone_number
        : '';
    const payload = {...formData, phone_number: contact} as const;
    dispatch(reduxApi.patch('user', {data: payload}))
      .then(() => {
        // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
        dispatch(getUserDetails());
        setIsSubmitting(false);
        setSelectedTab('payment');
      })
      .catch(() => {
        toastApi.show(
          toastMessage({
            message: t('User_details_not_updated', 'User details not updated'),
            semantic: 'danger',
          }),
        );

        setIsSubmitting(false);
      });
  };

  const savePaymentDetails = async () => {
    const {method, details} = paymentData;
    const {
      paypal_id,
      bank_name,
      account_number,
      name,
      routing_number,
      swift_code,
    } = details;

    const payload = {
      ...paymentData,
      details:
        method === 'paypal'
          ? {paypal_id}
          : {bank_name, account_number, name, routing_number, swift_code},
    } as const;

    // @ts-expect-error - TS2345 - Argument of type '{ readonly details: { paypal_id: string; bank_name?: undefined; account_number?: undefined; name?: undefined; routing_number?: undefined; swift_code?: undefined; } | { bank_name: string; account_number: string; name: string; routing_number: string; swift_code: string; paypal_id?: undefined; }; readonly method: "payp...' is not assignable to parameter of type '{ [key: string]: string | boolean | { [key: string]: string; }; }'.
    dispatch(createPaymentDetails(payload))
      .then((response) => {
        if (response) {
          setIsSetup(false);
          router.replace('/dashboard');
        }
      })
      .finally(() => setIsSubmitting(false));
  };

  const validate = () => {
    const newErrors = new Map();

    let requiredFields = ['name', 'email'];
    if (!isProfileConfig) {
      if (paymentData.method === 'paypal') {
        requiredFields = ['paypal_id'];
      } else {
        requiredFields = [
          'account_number',
          'bank_name',
          'name',
          'routing_number',
          'swift_code',
        ];
      }
    }
    requiredFields.forEach((field) => {
      const value = isProfileConfig
        ? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'UserDetails'.
          formData[field]
        : // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ name: string; bank_name: string; account_number: string; routing_number: string; swift_code: string; paypal_id: string; }'.
          paymentData.details[field];
      if (!value) {
        newErrors.set(field, t('Field_is_required', 'Field is required'));
      }
    });
    if (!validatePhoneNumber(formData.phone_number)) {
      newErrors.set(
        'phone_number',
        `${t(
          'Please_provide_a_valid_phone_number',
          'Please provide a valid phone number',
        )}.`,
      );
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = () => {
    const error = validate();
    if (error.size > 0) {
      return;
    }
    if (selectedTab === 'profile') {
      saveUserDetails();
    } else {
      savePaymentDetails();
    }
  };

  const handleChange = (fieldName: string, value: string) => {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'UserDetails'.
    formData[fieldName] = value;
    setFormData({...formData});
  };

  const handleOnChangePayment = (field: string, value: string) => {
    let updatedData: PaymentDetails = paymentData;
    if (field === 'method') {
      updatedData = {
        ...paymentInitialData,
        [field]: value,
      };
      setPaymentData(updatedData);
    } else {
      updatedData = {
        ...updatedData,
        details: {...paymentData.details, [field]: value},
      };
      setPaymentData(updatedData);
    }
  };

  const account: UserDetails | null | undefined =
    useSelector(selectUserDetails);

  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const isUserDataLoading = useInflight(getUserDetails()) && isProfileConfig;

  React.useEffect(() => {
    if (account) {
      setFormData(account);
    }
  }, [account]);

  return (
    <Modal isOpen={isSetup}>
      <ModalHeader
        onCloseButtonClick={() => {
          setIsSetup(false);
          router.replace('/dashboard');
        }}
      >
        {t('Getting_Started', 'Getting Started')}
      </ModalHeader>
      <ModalBody className={css.setupProfileCard}>
        <div className={css.setupProfileHeader}>
          <SubTitleLarge>{`${t(
            'Welcome_to_Sense_Referrals',
            'Welcome to Sense Referrals',
          )}!`}</SubTitleLarge>
          <BodyLarge color="secondary" className={css.setupCard}>{`${t(
            'Let_us_know_a_bit_more_so_we_can_help_you_get_started',
            'Let us know a bit more so we can help you get started',
          )}.`}</BodyLarge>
        </div>
        <Stepper
          classNames={{wrapper: css.setupCard}}
          activeStep={
            isProfileConfig
              ? PROFILE_SETUP_STEEPER.user
              : PROFILE_SETUP_STEEPER.payment
          }
        >
          <Step
            allowClick={true}
            completed={!isProfileConfig}
            index={PROFILE_SETUP_STEEPER.user}
            onClick={() => setSelectedTab('profile')}
          >
            <StepLabel>{t('Profile_Details', 'Profile Details')}</StepLabel>
          </Step>
          <Step
            index={PROFILE_SETUP_STEEPER.payment}
            onClick={() => setSelectedTab('payment')}
          >
            <StepLabel>{t('Payment_Options', 'Payment Options')}</StepLabel>
          </Step>
        </Stepper>
        {isUserDataLoading ? (
          <CircularLoader className={css.loader} />
        ) : isProfileConfig ? (
          <Card>
            <CardHeader>
              <SubTitleMedium>
                {t('Personal_Information', 'Personal Information')}
              </SubTitleMedium>
              <UploadProfile />
            </CardHeader>
            <CardContent className={css.flexColumn}>
              <div className={css.detailsRow}>
                {USER_FORM_INPUT_FIELDS.map((element) => (
                  <div>
                    {element.key === 'phone_number' ? (
                      <PhoneInputQuestion
                        value={getDefaultPhoneNumber(formData[element.key])}
                        onChange={(value) =>
                          handleChange(element.key, value.join(''))
                        }
                        error={errors.has(element.key)}
                      />
                    ) : (
                      <Input
                        key={element.key}
                        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'UserDetails'.
                        value={formData[element.key]}
                        disabled={element.key === 'email'}
                        onChange={(event) =>
                          handleChange(element.key, event.target.value)
                        }
                        label={element.label}
                        required={['name', 'email'].includes(element.key)}
                        error={errors.has(element.key)}
                        errorText={errors.get(element.key)}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className={css.defaultMarginTop}>
                <Textarea
                  label="Bio"
                  value={formData['bio']}
                  onChange={(event) => handleChange('bio', event.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        ) : (
          <PaymentCard
            errors={errors}
            paymentData={paymentData}
            handleOnChange={handleOnChangePayment}
          />
        )}
      </ModalBody>
      <ModalFooter
        classNames={{actions: classify({[css.action]: !isProfileConfig})}}
      >
        {!isProfileConfig && (
          <Button type="tertiary" onClick={() => setSelectedTab('profile')}>
            {t('Back', 'Back')}
          </Button>
        )}
        <div className={css.defaultGap}>
          <Button
            type="ghost"
            onClick={() => {
              setIsSetup(false);
              router.replace('/dashboard');
            }}
          >
            {t('Skip', 'Skip')}
          </Button>
          <Button
            disabled={isSubmitting}
            isLoading={isUserDataLoading}
            onClick={handleSubmit}
          >
            {isProfileConfig ? t('Next', 'Next') : t('Submit', 'Submit')}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default SetupProfile;

export const getDefaultPhoneNumber = (value: string): Array<string> => {
  let [countryCode, phone] = ['+1', ''];

  if (value && value.toString().startsWith('+')) {
    const countryShortCode = value.startsWith('+1')
      ? 'US'
      : getCountryFromPhone(value);
    countryCode = countryShortCode
      ? // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'CountryCode'.
        `+${getCountryCallingCode(countryShortCode)}`
      : value;
    phone = value.slice(countryCode.length);
  }
  return [countryCode, phone];
};

export const validatePhoneNumber = (value: string): boolean => {
  let [countryCode, phone] = ['+1', ''];
  let countryShortCode;
  if (value && value.toString().startsWith('+')) {
    countryShortCode = value.startsWith('+1')
      ? 'US'
      : getCountryFromPhone(value);
    countryCode = countryShortCode
      ? // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'CountryCode'.
        `+${getCountryCallingCode(countryShortCode)}`
      : value;
    phone = value.slice(countryCode.length);
  }
  return phone ? validatePhone(phone, countryShortCode || 'US') : true;
};
